import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Card,
  CardContent,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { AppButton, AppTextField } from '../../components/design-system';
import { SatelliteHealthIcon } from '../../branding/spatialNavIcons';
import {
  Refresh as RefreshIcon,
  Router as GatewayIcon,
  CheckCircle as HealthyIcon,
  Warning as DegradedIcon,
  Error as UnhealthyIcon,
  HelpOutline as UnknownIcon,
  AssignmentTurnedIn as AuthorizeIcon,
  PlayCircleOutline as TriggerIcon,
  PowerSettingsNew as RebootIcon,
  DevicesOther as PeripheralIcon,
} from '@mui/icons-material';
import { AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { useTheme } from '@mui/material/styles';
import { getDeviceStatusColorMaps } from '../../theme/uiSemanticColors';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { opsEfemPeripheralsService } from '../../services/OpsEfemPeripheralsService';
import { opsGatewayHealthService } from '../../services/OpsGatewayHealthService';
import { opsDevicesService } from '../../services/OpsDevicesService';
import type {
  GatewayHealthRow,
  GatewayOverallHealth,
  EfemPeripheralHealthRow,
} from '@cnapse/shared/schemas.js';
import { EnhancedTable, type TableColumn, type TableAction, type FilterConfig } from '../../components/ui/EnhancedTable';
import { getDeviceCategorySelectOptions, PRODUCT_NAMES } from '@cnapse/shared';
import { appNavigate } from '../../utils/appNavigate';

const HEALTH_COLORS: Record<GatewayOverallHealth, 'success' | 'warning' | 'error' | 'default'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
  unknown: 'default',
};

const HB_STATUS_COLORS: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  stale: 'warning',
  unknown: 'default',
};

function SummaryCard({ label, value, color, icon }: { label: string; value: number; color: string; icon: React.ReactNode }) {
  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ color }}>{value.toLocaleString()}</Typography>
      </CardContent>
    </Card>
  );
}

/** Decommissioned gateway awaiting provider recovery workflow (matches DeviceDetail). */
function isAwaitingReprovision(row: GatewayHealthRow): boolean {
  return (
    row.iotProvisioningStatus === 'not_provisioned' &&
    row.inventoryStatus === 'claimed'
  );
}

function isProvisionedGateway(row: GatewayHealthRow): boolean {
  return row.iotProvisioningStatus === 'provisioned';
}

export default function GatewayHealthDashboard() {
  const theme = useTheme();
  const statusMaps = useMemo(() => getDeviceStatusColorMaps(theme), [theme]);
  const [rows, setRows] = useState<GatewayHealthRow[]>([]);
  const [peripheralRows, setPeripheralRows] = useState<EfemPeripheralHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilters, setTableFilters] = useState<Record<string, string>>({
    tenantId: '',
    siteId: '',
    deviceCategory: '',
    overallHealth: '',
  });
  const [reproModal, setReproModal] = useState<{ open: boolean; serial: string; type: 'authorize' | 'trigger' | 'reboot' }>({
    open: false,
    serial: '',
    type: 'authorize',
  });
  const [reproReason, setReproReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [probeMessage, setProbeMessage] = useState<string | null>(null);
  const [expandedGatewaySerial, setExpandedGatewaySerial] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let cursor: string | undefined;
      const allRows: GatewayHealthRow[] = [];

      do {
        const result = await opsGatewayHealthService.getGatewayHealthList({
          cursor,
          limit: 200,
        });
        allRows.push(...result.data.gateways);
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);

      const tenantsData = await providerTenantsService.getTenants();

      let pCursor: string | undefined;
      const allPeripherals: EfemPeripheralHealthRow[] = [];
      do {
        const pResult = await opsEfemPeripheralsService.getEfemPeripheralHealthList({ cursor: pCursor, limit: 200 });
        allPeripherals.push(...pResult.data.peripherals);
        pCursor = pResult.data.nextCursor ?? undefined;
      } while (pCursor);

      setRows(allRows);
      setPeripheralRows(allPeripherals);
      setTenants(tenantsData as Tenant[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gateway health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, tableFilters, pageSize]);

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  const tenantNameById = useMemo(() => {
    return new Map<string, string>(tenants.map((tenant) => [String(tenant.id), tenant.name]));
  }, [tenants]);

  const filterOptions = useMemo((): FilterConfig[] => {
    const siteOptions = Array.from(new Set(rows.map((row) => row.siteId).filter(Boolean) as string[]))
      .sort((left, right) => left.localeCompare(right))
      .map((siteId) => ({ label: siteId, value: siteId }));

    return [
      {
        field: 'tenantId',
        label: 'Tenant',
        type: 'select',
        options: tenants.map((tenant) => ({ label: tenant.name, value: tenant.id })),
      },
      {
        field: 'siteId',
        label: 'Site',
        type: 'select',
        options: siteOptions,
      },
      {
        field: 'deviceCategory',
        label: 'Category',
        type: 'select',
        options: getDeviceCategorySelectOptions(['efem-gateway', 'location-gateway']),
      },
      {
        field: 'overallHealth',
        label: 'Health',
        type: 'select',
        options: [
          { label: 'Healthy', value: 'healthy' },
          { label: 'Degraded', value: 'degraded' },
          { label: 'Unhealthy', value: 'unhealthy' },
          { label: 'Unknown', value: 'unknown' },
        ],
      },
    ];
  }, [rows, tenants]);

  const filteredRows = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      if (tableFilters.tenantId && row.tenantId !== tableFilters.tenantId) return false;
      if (tableFilters.siteId && row.siteId !== tableFilters.siteId) return false;
      if (tableFilters.deviceCategory && row.deviceCategory !== tableFilters.deviceCategory) return false;
      if (tableFilters.overallHealth && row.overallHealth !== tableFilters.overallHealth) return false;

      if (!searchValue) return true;

      const tenantName = row.tenantId ? tenantNameById.get(row.tenantId) ?? '' : '';
      const haystack = [
        row.serialNumber,
        row.tenantId ?? '',
        tenantName,
        row.siteId ?? '',
        row.deviceCategory ?? '',
        row.gatewayHeartbeatStatus,
        row.guardHeartbeatStatus,
        row.overallHealth,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchValue);
    });
  }, [rows, searchTerm, tableFilters, tenantNameById]);

  const summary = useMemo(() => {
    return filteredRows.reduce(
      (accumulator, row) => {
        accumulator.total += 1;
        accumulator[row.overallHealth] += 1;
        return accumulator;
      },
      { total: 0, healthy: 0, degraded: 0, unhealthy: 0, unknown: 0 },
    );
  }, [filteredRows]);

  const peripheralsByGateway = useMemo(() => {
    const map = new Map<string, EfemPeripheralHealthRow[]>();
    for (const row of peripheralRows) {
      const list = map.get(row.gatewaySerialNumber) ?? [];
      list.push(row);
      map.set(row.gatewaySerialNumber, list);
    }
    return map;
  }, [peripheralRows]);

  const expandedPeripherals = expandedGatewaySerial
    ? peripheralsByGateway.get(expandedGatewaySerial) ?? []
    : [];

  const peripheralIssuesByGateway = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of peripheralRows) {
      if (p.peripheralCategory === 'efem-adapter') continue;
      if (p.overallHealth === 'degraded' || p.overallHealth === 'unhealthy' || p.overallHealth === 'unknown') {
        map.set(p.gatewaySerialNumber, (map.get(p.gatewaySerialNumber) ?? 0) + 1);
      }
    }
    return map;
  }, [peripheralRows]);

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredRows.slice(startIndex, startIndex + pageSize);
  }, [filteredRows, page, pageSize]);

  const columns: TableColumn<GatewayHealthRow>[] = [
    {
      field: 'serialNumber',
      headerName: 'Cnapse Serial',
      renderCell: (row: GatewayHealthRow) => <Typography variant="body2" fontFamily="monospace">{row.serialNumber}</Typography>,
    },
    {
      field: 'tenantId',
      headerName: 'Tenant',
      renderCell: (row: GatewayHealthRow) => {
        const tenant = tenants.find((t) => t.id === row.tenantId);
        return <Typography variant="body2">{tenant?.name ?? row.tenantId ?? '—'}</Typography>;
      },
    },
    { field: 'siteId', headerName: 'Site', renderCell: (row: GatewayHealthRow) => row.siteId ?? '—' },
    { field: 'deviceCategory', headerName: 'Category', renderCell: (row: GatewayHealthRow) => row.deviceCategory ?? '—' },
    {
      field: 'gatewayHeartbeatStatus',
      headerName: 'GW Heartbeat',
      renderCell: (row: GatewayHealthRow) => (
        <Tooltip title={row.gatewayHeartbeatLastSeenAt ? `Last seen: ${new Date(row.gatewayHeartbeatLastSeenAt).toLocaleString()}` : 'No heartbeat'}>
          <Chip
            label={row.gatewayHeartbeatStatus}
            color={HB_STATUS_COLORS[row.gatewayHeartbeatStatus]}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      field: 'guardHeartbeatStatus',
      headerName: 'Guard Heartbeat',
      renderCell: (row: GatewayHealthRow) => (
        <Tooltip title={row.guardHeartbeatLastSeenAt ? `Last seen: ${new Date(row.guardHeartbeatLastSeenAt).toLocaleString()}` : 'No heartbeat'}>
          <Chip
            label={row.guardHeartbeatStatus}
            color={HB_STATUS_COLORS[row.guardHeartbeatStatus]}
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      field: 'overallHealth',
      headerName: 'Overall',
      renderCell: (row: GatewayHealthRow) => {
        const tooltipTitle = row.statusDetails
          ? `${row.orchestratorStatus?.toUpperCase() ?? 'DEGRADED'}: ${row.statusDetails}`
          : row.overallHealth;
        return (
          <Tooltip title={tooltipTitle} arrow>
            <Chip
              label={row.overallHealth}
              color={HEALTH_COLORS[row.overallHealth]}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'peripherals',
      headerName: 'Peripherals',
      renderCell: (row: GatewayHealthRow) => {
        if (row.deviceCategory === 'efem-gateway') {
          const issues = peripheralIssuesByGateway.get(row.serialNumber) ?? 0;
          if (issues === 0) return '—';
          return (
            <Chip
              size="small"
              color="warning"
              label={`${issues} issue${issues === 1 ? '' : 's'}`}
              onClick={(e) => {
                e.stopPropagation();
                setExpandedGatewaySerial((current) =>
                  current === row.serialNumber ? null : row.serialNumber,
                );
              }}
            />
          );
        }
        if (row.deviceCategory === 'location-gateway' && row.siteId) {
          return (
            <Chip
              size="small"
              variant="outlined"
              icon={<SatelliteHealthIcon />}
              label="Satellites"
              onClick={() => {
                const params = new URLSearchParams();
                if (row.tenantId) params.set('tenantId', row.tenantId);
                if (row.siteId) params.set('siteId', row.siteId);
                const qs = params.toString();
                appNavigate(`/provider/ops/site-anchor-health${qs ? `?${qs}` : ''}`);
              }}
            />
          );
        }
        return '—';
      },
    },
  ];

  const actions: TableAction<GatewayHealthRow>[] = [
    {
      label: 'Authorize Recovery',
      icon: <AuthorizeIcon fontSize="small" />,
      color: 'info',
      visible: (row) => isAwaitingReprovision(row) && row.reprovisioningPermitted !== true,
      disabled: (row) => row.reprovisioningPermitted === true,
      onClick: async (row: GatewayHealthRow) => {
        setReproModal({ open: true, serial: row.serialNumber, type: 'authorize' });
        setReproReason('');
      },
    },
    {
      label: 'Trigger Recovery',
      icon: <TriggerIcon fontSize="small" />,
      color: 'success',
      visible: (row) => isAwaitingReprovision(row) && row.reprovisioningPermitted === true,
      disabled: (row) => row.reprovisioningPermitted !== true,
      onClick: async (row: GatewayHealthRow) => {
        setReproModal({ open: true, serial: row.serialNumber, type: 'trigger' });
        setReproReason('');
      },
    },
    {
      label: 'Probe Peripherals',
      icon: <PeripheralIcon fontSize="small" />,
      color: 'secondary',
      visible: (row) => row.deviceCategory === 'efem-gateway' && isProvisionedGateway(row),
      disabled: () => actionLoading,
      onClick: async (row: GatewayHealthRow) => {
        setActionLoading(true);
        setProbeMessage(null);
        setError(null);
        try {
          const result = await opsGatewayHealthService.probeEfemPeripherals(row.serialNumber, 'Gateway Health on-demand probe');
          setProbeMessage(
            `${row.serialNumber}: ${result.data.peripherals.length} peripheral(s) connected (probed ${new Date(result.data.probedAt).toLocaleString()})`,
          );
          await loadData();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Peripheral probe failed');
        } finally {
          setActionLoading(false);
        }
      },
    },
    {
      label: `View ${PRODUCT_NAMES.AXON} Peripherals`,
      icon: <PeripheralIcon fontSize="small" />,
      color: 'info',
      visible: (row) => row.deviceCategory === 'efem-gateway',
      onClick: async (row: GatewayHealthRow) => {
        appNavigate(`/provider/ops/efem-peripherals?gatewaySerialNumber=${encodeURIComponent(row.serialNumber)}`);
      },
    },
    {
      label: 'View Site Satellites',
      icon: <SatelliteHealthIcon fontSize="small" />,
      color: 'info',
      visible: (row) => row.deviceCategory === 'location-gateway' && !!row.siteId,
      onClick: async (row: GatewayHealthRow) => {
        const params = new URLSearchParams();
        if (row.tenantId) params.set('tenantId', row.tenantId);
        if (row.siteId) params.set('siteId', row.siteId);
        const qs = params.toString();
        appNavigate(`/provider/ops/site-anchor-health${qs ? `?${qs}` : ''}`);
      },
    },
    {
      label: 'Reboot Gateway',
      icon: <RebootIcon fontSize="small" />,
      color: 'warning',
      visible: (row) => isProvisionedGateway(row),
      onClick: async (row: GatewayHealthRow) => {
        setReproModal({ open: true, serial: row.serialNumber, type: 'reboot' });
        setReproReason('');
      },
    },
  ];

  const handleReproAction = async () => {
    if (!reproReason.trim()) return;
    setActionLoading(true);
    try {
      if (reproModal.type === 'authorize') {
        await opsDevicesService.allowDeviceReprovisioning(reproModal.serial, reproReason);
      } else if (reproModal.type === 'trigger') {
        await opsDevicesService.triggerDeviceReprovisioning(reproModal.serial, reproReason);
      } else {
        await opsDevicesService.rebootDevice(reproModal.serial, reproReason);
      }
      setReproModal({ ...reproModal, open: false });
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <AppListPage>
      {/* Summary Cards */}
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))" gap={2} mb={3}>
        <SummaryCard label="Total" value={summary.total} color={statusMaps.health.total} icon={<GatewayIcon />} />
        <SummaryCard label="Healthy" value={summary.healthy} color={statusMaps.health.healthy} icon={<HealthyIcon />} />
        <SummaryCard label="Degraded" value={summary.degraded} color={statusMaps.health.degraded} icon={<DegradedIcon />} />
        <SummaryCard label="Unhealthy" value={summary.unhealthy} color={statusMaps.health.unhealthy} icon={<UnhealthyIcon />} />
        <SummaryCard label="Unknown" value={summary.unknown} color={statusMaps.health.unknown} icon={<UnknownIcon />} />
      </Box>

      {probeMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProbeMessage(null)}>
          {probeMessage}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <EnhancedTable
        data={pagedRows}
        totalCount={filteredRows.length}
        columns={columns}
        actions={actions}
        getRowId={(row) => row.serialNumber}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
        searchable={true}
        searchTerm={searchTerm}
        searchPlaceholder="Search serial, tenant, site, category..."
        onSearchChange={setSearchTerm}
        filters={tableFilters}
        filterOptions={filterOptions}
        onFilterChange={(filters) => setTableFilters(filters as Record<string, string>)}
        onRowClick={(row) => {
          if (row.deviceCategory === 'efem-gateway') {
            setExpandedGatewaySerial((current) =>
              current === row.serialNumber ? null : row.serialNumber,
            );
          }
        }}
        emptyStateTitle="No gateways found"
        emptyStateDescription="Use search or filters, or refresh to try again."
        maxHeight="calc(100vh - 360px)"
      />

      {expandedGatewaySerial && (
        <Paper elevation={1} sx={{ mt: 2, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {PRODUCT_NAMES.AXON} Peripherals — {expandedGatewaySerial}
            </Typography>
            <Box display="flex" gap={1}>
              <AppButton
                size="small"
                variant="outlined"
                onClick={() =>
                  appNavigate(`/provider/ops/efem-peripherals?gatewaySerialNumber=${encodeURIComponent(expandedGatewaySerial)}`)
                }
              >
                Open full view
              </AppButton>
              <AppButton size="small" variant="text" onClick={() => setExpandedGatewaySerial(null)}>
                Close
              </AppButton>
            </Box>
          </Box>
          {expandedPeripherals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No peripheral rows loaded for this gateway.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {expandedPeripherals.map((peripheral) => (
                <Box
                  key={`${peripheral.gatewaySerialNumber}-${peripheral.roleKey ?? peripheral.peripheralSerialNumber ?? peripheral.peripheralCategory}`}
                  display="flex"
                  gap={2}
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <Typography variant="body2" fontFamily="monospace">
                    {peripheral.peripheralSerialNumber ?? '—'}
                  </Typography>
                  <Chip size="small" label={peripheral.peripheralCategory.replace(/-/g, ' ')} variant="outlined" />
                  <Chip size="small" label={peripheral.roleLabel ?? 'role'} variant="outlined" />
                  <Chip size="small" label={`runtime: ${peripheral.runtimeStatus ?? 'unknown'}`} />
                  <Typography variant="caption" color="text.secondary">
                    {peripheral.runtimeLastSeenAt
                      ? `probed ${new Date(peripheral.runtimeLastSeenAt).toLocaleString()}`
                      : 'no probe yet'}
                  </Typography>
                  <Chip
                    size="small"
                    color={
                      peripheral.overallHealth === 'healthy'
                        ? 'success'
                        : peripheral.overallHealth === 'unhealthy'
                          ? 'error'
                          : peripheral.overallHealth === 'degraded'
                            ? 'warning'
                            : 'default'
                    }
                    label={peripheral.overallHealth}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={reproModal.open} onClose={() => !actionLoading && setReproModal({ ...reproModal, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reproModal.type === 'authorize' ? 'Authorize Remote Recovery' : 
           reproModal.type === 'trigger' ? 'Trigger Remote Recovery' : 'Trigger Remote Reboot'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {reproModal.type === 'authorize'
                ? `This will grant a one-time authorization for ${reproModal.serial} to re-provision. This is required if certificates were previously revoked.`
                : reproModal.type === 'trigger'
                ? `This will send a command via AWS SSM to ${reproModal.serial} to clear identity sentinel files and restart the provisioner. This action is irreversible and will be logged.`
                : `This will send a 'Restart-Computer -Force' command via AWS SSM to ${reproModal.serial}. The gateway will be offline for 2-5 minutes during the reboot.`}
            </Typography>
            <AppTextField
              label="Reason for Action"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={reproReason}
              onChange={(e) => setReproReason(e.target.value)}
              placeholder="e.g., Recovering after accidental revocation, or requested by support..."
              helperText="Required for audit log"
              sx={{ mt: 2 }}
              disabled={actionLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setReproModal({ ...reproModal, open: false })} disabled={actionLoading}>Cancel</AppButton>
          <AppButton
            onClick={handleReproAction}
            variant="contained"
            color={reproModal.type === 'authorize' ? 'info' : reproModal.type === 'trigger' ? 'success' : 'warning'}
            disabled={!reproReason.trim() || actionLoading}
          >
            {actionLoading ? 'Processing...' : 
             reproModal.type === 'authorize' ? 'Authorize' : 
             reproModal.type === 'trigger' ? 'Trigger Command' : 'Reboot Now'}
          </AppButton>
        </DialogActions>
      </Dialog>
    </AppListPage>
  );
}
